import type { FetchQuoteParams } from 'actions/InsuranceActions'
import { fetchInsuranceQuote, retryInsuranceQuote } from 'actions/InsuranceActions'
import { checkoutHasPreselectedUpstreamInsurance } from 'checkout/selectors/view/insurance'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getInsuranceQuoteKey } from 'lib/insurance/insuranceUtils'
import { countOccupants } from 'lib/offer/occupancyUtils'
import { useCallback, useEffect } from 'react'

interface InsuranceQuoteOptions extends Partial<FetchQuoteParams> {
  disabled?: boolean
}

type QuoteReturn = [
  quote: App.InsuranceQuote | undefined,
  fetching: boolean,
  error: any,
  retry: () => void,
]

function useInsuranceQuote(
  productId: string | undefined,
  params: InsuranceQuoteOptions,
  options: { disabled?: boolean, priceIncreaseExperimentEligible?: boolean } = {},
): QuoteReturn {
  const dispatch = useAppDispatch()

  const travellers = params.travellers
    ? Array.isArray(params.travellers) ? countOccupants(params.travellers) : params.travellers
    : undefined

  const travellerCount = travellers
    ? (travellers.adults ?? 0) + (travellers.children ?? 0) + (travellers.infants ?? 0)
    : 0

  const canFetchQuote = !!(
    !options.disabled &&
    productId &&
    params.startDate &&
    params.endDate &&
    params.coverAmount &&
    params.destinationCountries?.length &&
    params.travellers &&
    travellerCount > 0
  )

  // Insurance price-increase experiment activation. The applyPriceIncrease flag lives on the quote
  // params (set by the caller from a shared state selector, so the fetch and the checkout read
  // selectors build the same cache key); this hook only fires the Optimizely activation. Activation
  // is gated to a fresh insurance decision: checkout uses the default below (no protection selected
  // upstream), while post-purchase upsells pass priceIncreaseExperimentEligible explicitly so the
  // gate does not depend on incidental checkout-cart state.
  const hasPreselectedUpstreamInsurance = useAppSelector(checkoutHasPreselectedUpstreamInsurance)
  const priceIncreaseExperimentEligible = options.priceIncreaseExperimentEligible ?? !hasPreselectedUpstreamInsurance
  useExperiment(ChertoExperiments.croInsurancePriceIncrease, { canActivate: canFetchQuote && priceIncreaseExperimentEligible })

  const key = getInsuranceQuoteKey(productId ?? '', params)
  const quote = useAppSelector(state => state.insurance.quotes[key])
  const fetching = useAppSelector(state => !!state.insurance.fetchingQuotes[key])
  const error = useAppSelector(state => state.insurance.fetchQuotesErrors[key])

  useEffect(() => {
    if (canFetchQuote) {
      dispatch(fetchInsuranceQuote(productId, params as FetchQuoteParams))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [key, options.disabled, canFetchQuote])

  const retry = useCallback(() => {
    if (!canFetchQuote || !productId) return
    dispatch(retryInsuranceQuote(productId, params as FetchQuoteParams))
  }, [canFetchQuote, dispatch, params, productId])

  return [
    quote,
    canFetchQuote ? (fetching ?? true) : false,
    error,
    retry,
  ]
}

export default useInsuranceQuote
